'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, type ReactNode } from 'react';
import { AppContentLoading } from '@/components/shared/AppRouteLoading';
import { EmptyState } from '@/components/shared/ui/EmptyState';
import { useProject } from '@/contexts/ProjectContext';

export function ProjectDeepLinkGate({ children }: { children: ReactNode }) {
  const requestedProjectId = useSearchParams().get('projectId')?.trim() || null;
  const { currentProject, projects, projectsHydrated, isReady, loading, error, switchProject } =
    useProject();
  const accessible = projects.some(project => project.id === requestedProjectId);
  const handledProject = useRef<string | null>(null);
  const settled = isReady && projectsHydrated && !loading;
  useEffect(() => {
    if (!settled || !accessible || !requestedProjectId) {
      handledProject.current = null;
      return;
    }
    if (handledProject.current === requestedProjectId) return;
    handledProject.current = requestedProjectId;
    if (currentProject?.id !== requestedProjectId)
      switchProject(requestedProjectId, { preserveRoute: true });
  }, [settled, accessible, requestedProjectId, currentProject?.id, switchProject]);

  if (!requestedProjectId) return children;
  if (!loading && error)
    return (
      <EmptyState role="alert" title="Unable to load project" description="Refresh to try again." />
    );
  if (!settled) return <AppContentLoading />;
  if (!accessible)
    return (
      <EmptyState
        role="alert"
        title="Project not accessible"
        description="Choose an accessible project in Attune to continue."
        action={<a href="/app/workspace">Open workspace</a>}
      />
    );
  // Do not mount project data consumers while the persisted selection is changing.
  if (currentProject?.id !== requestedProjectId) return <AppContentLoading />;
  return children;
}
